/* ═══════════════════════════════════════════════════════════════════
   NUVORA PDP — JavaScript
   Gallery · Variants · Sticky Bar · Scroll Reveal · Metrics Counter · AJAX Cart
   ═══════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  /* ── Utility: format money from cents ── */
  function formatMoney(cents) {
    const pounds = cents / 100;
    return '£' + (pounds % 1 === 0 ? pounds.toFixed(0) : pounds.toFixed(2));
  }

  /* ════════════════════════════════════════════════
     GALLERY
     ════════════════════════════════════════════════ */
  const NUVORA_UNMUTE_ICON_MUTED = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M3 9v6h4l5 5V4L7 9H3z" fill="currentColor" stroke="none"/><path d="M16 8l4 8M20 8l-4 8"/></svg><span>Tap for sound</span>';
  const NUVORA_UNMUTE_ICON_UNMUTED = '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M3 9v6h4l5 5V4L7 9H3z"/><path d="M16.5 12a4.5 4.5 0 0 0-2.25-3.9v7.8A4.5 4.5 0 0 0 16.5 12z"/><path d="M19 12a7 7 0 0 0-3.5-6.06v1.98A5 5 0 0 1 17 12a5 5 0 0 1-1.5 3.58v1.98A7 7 0 0 0 19 12z"/></svg>';

  const NuvoraGallery = {
    activeIndex: 0,
    inView: false,

    /* Fonte única de verdade: um vídeo só reproduz se o SEU slide estiver
       activo (visível na galeria) E a galeria estiver mesmo à vista no
       ecrã (scroll) — evita gastar dados/bateria a tocar escondido. */
    updateActiveVideo() {
      const slides = document.querySelectorAll('.nuvora-gallery__slide');
      slides.forEach((slide, i) => {
        const video = slide.querySelector('video');
        if (!video) return;
        if (i === this.activeIndex && this.inView) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });
    },

    init() {
      /* Liquid renders the second image as the initially visible slide
         (first image is reserved for product cards/grids) — read it from
         the DOM so prev/next stay in sync from the first click */
      const activeSlide = document.querySelector('.nuvora-gallery__slide:not(.nuvora-gallery__slide--hidden)');
      if (activeSlide) {
        this.activeIndex = parseInt(activeSlide.dataset.index, 10) || 0;
      }

      /* Pausa quando o utilizador rola para longe da galeria (mesmo que o
         slide de vídeo continue "activo"); retoma sozinho ao voltar. */
      const galleryMain = document.getElementById('nuvora-gallery-main');
      if (galleryMain && 'IntersectionObserver' in window) {
        const scrollObserver = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            this.inView = entry.isIntersecting;
            this.updateActiveVideo();
          });
        }, { threshold: 0.4 });
        scrollObserver.observe(galleryMain);
      } else {
        this.inView = true;
        this.updateActiveVideo();
      }

      const thumbs = document.querySelectorAll('.nuvora-gallery__thumb');
      thumbs.forEach(thumb => {
        thumb.addEventListener('click', () => {
          this.goTo(parseInt(thumb.dataset.index, 10));
        });
      });

      /* Prev / Next buttons */
      const prevBtn = document.getElementById('nuvora-gallery-prev');
      const nextBtn = document.getElementById('nuvora-gallery-next');
      if (prevBtn) prevBtn.addEventListener('click', () => this.prev());
      if (nextBtn) nextBtn.addEventListener('click', () => this.next());

      /* Swipe support */
      const main = document.getElementById('nuvora-gallery-main');
      if (!main) return;

      let startX = 0;
      let startY = 0;

      main.addEventListener('touchstart', e => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
      }, { passive: true });

      main.addEventListener('touchend', e => {
        const dx = startX - e.changedTouches[0].clientX;
        const dy = startY - e.changedTouches[0].clientY;
        if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 40) {
          dx > 0 ? this.next() : this.prev();
        }
      }, { passive: true });

      /* Keyboard navigation when gallery is focused */
      const gallery = document.getElementById('nuvora-gallery');
      if (gallery) {
        gallery.setAttribute('tabindex', '0');
        gallery.addEventListener('keydown', e => {
          if (e.key === 'ArrowRight') this.next();
          if (e.key === 'ArrowLeft')  this.prev();
        });
      }

      /* Selo de som — toggle persistente (sem controlos nativos do
         player, este botão passa a ser a única forma de ligar/desligar
         o som). Ícone e texto reflectem sempre o estado actual. */
      document.querySelectorAll('.nuvora-gallery__unmute').forEach(btn => {
        btn.addEventListener('click', () => {
          const video = btn.previousElementSibling;
          if (!video || video.tagName !== 'VIDEO') return;
          video.muted = !video.muted;
          if (!video.muted) video.play().catch(() => {});
        });
      });
      document.querySelectorAll('.nuvora-gallery__video').forEach(video => {
        const badge = video.nextElementSibling;
        if (!badge || !badge.classList.contains('nuvora-gallery__unmute')) return;
        video.addEventListener('volumechange', () => {
          badge.classList.toggle('is-unmuted', !video.muted);
          badge.innerHTML = video.muted ? NUVORA_UNMUTE_ICON_MUTED : NUVORA_UNMUTE_ICON_UNMUTED;
          badge.setAttribute('aria-label', video.muted ? 'Tap to turn on sound' : 'Tap to mute');
        });
      });
    },

    goTo(index) {
      const slides = document.querySelectorAll('.nuvora-gallery__slide');
      const thumbs = document.querySelectorAll('.nuvora-gallery__thumb');
      const gallery = document.getElementById('nuvora-gallery');
      if (gallery) gallery.classList.add('nuvora-gallery--interacted');

      slides.forEach((slide, i) => {
        slide.classList.toggle('nuvora-gallery__slide--hidden', i !== index);
      });

      thumbs.forEach((thumb, i) => {
        thumb.classList.toggle('active', i === index);
        thumb.setAttribute('aria-pressed', String(i === index));
      });

      /* Scroll active thumb into view */
      const activeThumb = document.querySelector('.nuvora-gallery__thumb.active');
      if (activeThumb) {
        activeThumb.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
      }

      const counter = document.getElementById('nuvora-gallery-counter');
      if (counter) counter.textContent = (index + 1) + ' / ' + slides.length;

      this.activeIndex = index;
      this.updateActiveVideo();
    },

    goToByMediaId(mediaId) {
      const data = window.nuvoraProductData;
      if (!data) return;
      const idx = data.media.findIndex(m => m.id === mediaId);
      if (idx >= 0) this.goTo(idx);
    },

    next() {
      const count = document.querySelectorAll('.nuvora-gallery__slide').length;
      this.goTo((this.activeIndex + 1) % count);
    },

    prev() {
      const count = document.querySelectorAll('.nuvora-gallery__slide').length;
      this.goTo((this.activeIndex - 1 + count) % count);
    }
  };

  /* ════════════════════════════════════════════════
     VARIANT SELECTOR
     ════════════════════════════════════════════════ */
  const NuvoraVariants = {
    selectedOptions: [],

    init() {
      const data = window.nuvoraProductData;
      if (!data) return;

      /* Build initial selected options from active swatches */
      const activeSwatches = document.querySelectorAll('.nuvora-swatch.active');
      this.selectedOptions = Array.from(activeSwatches).map(s => s.dataset.value);

      document.querySelectorAll('.nuvora-swatch').forEach(swatch => {
        swatch.addEventListener('click', () => this.onSwatchClick(swatch));
      });
    },

    onSwatchClick(swatch) {
      const optionIndex = parseInt(swatch.dataset.optionIndex, 10);
      const value = swatch.dataset.value;

      /* Update active state within this option group */
      const group = swatch.closest('.nuvora-option__swatches');
      group.querySelectorAll('.nuvora-swatch').forEach(s => {
        s.classList.remove('active');
        s.setAttribute('aria-pressed', 'false');
      });
      swatch.classList.add('active');
      swatch.setAttribute('aria-pressed', 'true');

      /* Update label */
      const label = document.getElementById('nuvora-option-value-' + optionIndex);
      if (label) label.textContent = value;

      /* Update selected options array */
      this.selectedOptions[optionIndex] = value;

      /* Find matching variant */
      const variant = this.findVariant();
      if (variant) this.applyVariant(variant);

      /* Keep thumbnail swatches (e.g. Colour) showing the photo that
         matches the currently selected combination of options, not just
         the first variant found for that swatch at page load. */
      this.updateSwatchThumbnails();
    },

    updateSwatchThumbnails() {
      const data = window.nuvoraProductData;
      if (!data) return;
      document.querySelectorAll('.nuvora-swatch--thumb').forEach(swatch => {
        const img = swatch.querySelector('img');
        if (!img) return;

        const optionIndex = parseInt(swatch.dataset.optionIndex, 10);
        const candidateOptions = this.selectedOptions.slice();
        candidateOptions[optionIndex] = swatch.dataset.value;

        const variant = data.variants.find(v =>
          v.options.every((opt, i) => opt === candidateOptions[i])
        );
        if (!variant || !variant.featured_image) return;

        const base = variant.featured_image.src;
        const sep = base.indexOf('?') === -1 ? '?' : '&';
        img.src = base + sep + 'width=80';
        img.srcset = base + sep + 'width=80 1x, ' + base + sep + 'width=160 2x';
      });
    },

    findVariant() {
      const { variants } = window.nuvoraProductData;
      return variants.find(v =>
        v.options.every((opt, i) => opt === this.selectedOptions[i])
      ) || null;
    },

    applyVariant(variant) {
      /* Update hidden form inputs */
      document.querySelectorAll('#nuvora-variant-id, .nuvora-express-variant-id').forEach(input => {
        input.value = variant.id;
      });

      /* Update URL without reload */
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({ variantId: variant.id }, '', url.toString());

      /* Update price */
      this.updatePrice(variant);

      /* Update button */
      this.updateButton(variant);

      /* Update gallery image */
      if (variant.featured_media) {
        NuvoraGallery.goToByMediaId(variant.featured_media.id);
      }

      /* Update Klarna widget */
      this.updateKlarna(variant.price);

      /* Update sticky bar price */
      this.updateStickyPrice(variant);
    },

    updatePrice(variant) {
      const container = document.getElementById('nuvora-price');
      if (!container) return;

      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        const saving = variant.compare_at_price - variant.price;
        container.innerHTML =
          '<span class="nuvora-price__sale">' + formatMoney(variant.price) + '</span>' +
          '<s class="nuvora-price__original">' + formatMoney(variant.compare_at_price) + '</s>' +
          '<span class="nuvora-price__save-badge">Save ' + formatMoney(saving) + '</span>';
      } else {
        container.innerHTML =
          '<span class="nuvora-price__regular">' + formatMoney(variant.price) + '</span>';
      }
    },

    updateButton(variant) {
      const btn     = document.getElementById('nuvora-atc-btn');
      const btnText = btn && btn.querySelector('.nuvora-btn-atc__text');
      if (!btn || !btnText) return;

      if (variant.available) {
        btn.disabled = false;
        btnText.textContent = 'Add to cart — ' + formatMoney(variant.price);
      } else {
        btn.disabled = true;
        btnText.textContent = 'Sold out';
      }
    },

    updateKlarna(price) {
      const placement = document.querySelector('klarna-placement');
      if (!placement) return;
      placement.setAttribute('data-purchase-amount', price);
      if (window.Klarna && window.Klarna.OnsiteMessaging) {
        window.Klarna.OnsiteMessaging.refresh();
      }
    },

    updateStickyPrice(variant) {
      const el = document.getElementById('nuvora-sticky-price');
      if (!el) return;
      let html = formatMoney(variant.price);
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        html += '&nbsp;<s>' + formatMoney(variant.compare_at_price) + '</s>';
      }
      el.innerHTML = html;
    }
  };

  /* ════════════════════════════════════════════════
     STICKY BAR — mobile
     ════════════════════════════════════════════════ */
  const NuvStickyBar = {
    bar: null,
    atcBtn: null,

    init() {
      this.bar    = document.getElementById('nuvora-sticky-bar');
      this.atcBtn = document.getElementById('nuvora-atc-btn');
      if (!this.bar || !this.atcBtn) return;

      /* Only activate on mobile */
      if (!window.matchMedia('(max-width: 768px)').matches) return;

      /* Move to <body> — ensures position:fixed works regardless of section CSS */
      document.body.appendChild(this.bar);

      /* Control via inline style — overrides any CSS specificity issues */
      this.bar.style.cssText = [
        'display:block',
        'position:fixed',
        'bottom:0',
        'left:0',
        'right:0',
        'z-index:9999',
        'transform:translateY(100%)',
        'transition:transform 0.3s ease'
      ].join(';');

      const setVisible = show => {
        this.bar.style.transform = show ? 'translateY(0)' : 'translateY(100%)';
        this.bar.setAttribute('aria-hidden', String(!show));
      };

      let ticking = false;
      /* Stays hidden on first load — and stays hidden even while scrolling,
         as long as the real button simply hasn't been reached yet (e.g. still
         below the first fold). It only "activates" once the button has fully
         scrolled above the viewport at least once; from then on it tracks the
         button's visibility both ways, including on a return to the top. */
      let hasPassedButton = false;
      const update = () => {
        const rect = this.atcBtn.getBoundingClientRect();
        if (rect.bottom <= 0) hasPassedButton = true;
        const buttonVisible = rect.bottom > 0 && rect.top < window.innerHeight;
        setVisible(hasPassedButton && !buttonVisible);
        ticking = false;
      };

      window.addEventListener('scroll', () => {
        if (!ticking) { requestAnimationFrame(update); ticking = true; }
      }, { passive: true });

      update();
      /* Re-check after images load — layout may shift before load event */
      window.addEventListener('load', update);

      /* Sticky CTA triggers main form */
      const stickyCta = document.getElementById('nuvora-sticky-cta');
      if (stickyCta) {
        stickyCta.addEventListener('click', () => {
          document.getElementById('nuvora-product-form')?.requestSubmit();
        });
      }
    }
  };

  /* ════════════════════════════════════════════════
     AJAX CART
     ════════════════════════════════════════════════ */
  const NuvCart = {
    init() {
      const form = document.getElementById('nuvora-product-form');
      if (!form) return;

      form.addEventListener('submit', async e => {
        e.preventDefault();

        const btn = document.getElementById('nuvora-atc-btn');
        if (btn) btn.classList.add('nuvora-btn-atc--loading');

        const variantId = document.getElementById('nuvora-variant-id').value;

        /* O envio é feito à mão via fetch (não é um submit nativo do
           formulário), por isso qualquer input properties[...] escondido no
           form (ex.: o aviso "inclui bónus" do Tarn 528Hz) tem de ser lido
           manualmente aqui — de outra forma perdia-se, mesmo estando no
           HTML do formulário. */
        const mainItemProperties = {};
        new FormData(form).forEach((value, key) => {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match) mainItemProperties[match[1]] = value;
        });

        const mainItem = { id: parseInt(variantId, 10), quantity: 1 };
        if (Object.keys(mainItemProperties).length) {
          mainItem.properties = mainItemProperties;
        }

        /* Se o kit de óleos estiver completo (2/4/6 escolhidos), estes
           seguem no MESMO pedido do Tarn — deixaram de ir sozinhos para
           o carrinho quando o utilizador confirmava a selecção. */
        const oilItems = typeof window.nuvoraOilkitReadyItems === 'function'
          ? window.nuvoraOilkitReadyItems()
          : [];
        const items = [mainItem].concat(oilItems);

        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ items: items })
          });

          if (!res.ok) throw new Error('Cart add failed');

          /* Trigger theme cart drawer / bubble update */
          document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));

          /* Update cart count bubble if present */
          const countRes = await fetch('/cart.js');
          const cartData = await countRes.json();
          document.querySelectorAll('[data-cart-count]').forEach(el => {
            el.textContent = cartData.item_count;
          });

          /* Open theme cart if available, else redirect */
          if (typeof theme !== 'undefined' && theme.cart && theme.cart.open) {
            theme.cart.open();
          } else {
            window.location.href = '/cart';
          }
        } catch (err) {
          console.error('[Nuvora] Cart add error:', err);
        } finally {
          if (btn) btn.classList.remove('nuvora-btn-atc--loading');
        }
      });
    }
  };

  /* ════════════════════════════════════════════════
     SCROLL REVEAL (AOS-like, no dependency)
     ════════════════════════════════════════════════ */
  const NuvReveal = {
    init() {
      const els = document.querySelectorAll('.nuvora-reveal');
      if (!els.length) return;

      /* Opt in: mark body so CSS hides elements — only when JS is running */
      document.body.classList.add('nuvora-js');

      const reveal = el => el.classList.add('is-revealed');

      /* Reveal only elements currently visible in the viewport */
      const revealVisible = () => {
        document.querySelectorAll('.nuvora-reveal:not(.is-revealed)').forEach(el => {
          const rect = el.getBoundingClientRect();
          if (rect.top < window.innerHeight - 20 && rect.bottom > 0) reveal(el);
        });
      };

      /* Immediately reveal elements already on screen */
      revealVisible();

      /* IntersectionObserver: fires when element is 20px inside viewport */
      const observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              reveal(entry.target);
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0, rootMargin: '0px 0px -20px 0px' }
      );

      document.querySelectorAll('.nuvora-reveal:not(.is-revealed)').forEach(el => {
        observer.observe(el);
      });

      /* Safety net: every 1.5 s reveal only what is VISIBLE — never reveals off-screen elements */
      const safety = setInterval(() => {
        const hidden = document.querySelectorAll('.nuvora-reveal:not(.is-revealed)');
        if (!hidden.length) { clearInterval(safety); return; }
        revealVisible();
      }, 1500);
    }
  };

  /* ════════════════════════════════════════════════
     METRICS COUNTER (requestAnimationFrame)
     ════════════════════════════════════════════════ */
  const NuvCounter = {
    duration: 1600,

    init() {
      const counters = document.querySelectorAll('.nuvora-counter');
      if (!counters.length) return;

      const observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.animateCounter(entry.target);
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.5 }
      );

      counters.forEach(counter => observer.observe(counter));
    },

    animateCounter(el) {
      const target   = parseFloat(el.dataset.target.replace(/,/g, ''));
      const decimals = parseInt(el.dataset.decimals || '0', 10);
      const start    = performance.now();

      const fmt = (n) => decimals > 0
        ? n.toFixed(decimals)
        : Math.floor(n).toLocaleString('en-GB');

      const step = (now) => {
        const elapsed  = now - start;
        const progress = Math.min(elapsed / this.duration, 1);
        const eased    = 1 - Math.pow(1 - progress, 3); /* ease-out cubic */

        el.textContent = fmt(target * eased);

        if (progress < 1) requestAnimationFrame(step);
        else el.textContent = fmt(target);
      };

      requestAnimationFrame(step);
    }
  };

  /* ════════════════════════════════════════════════
     STAR ANIMATION (sequential entrance on scroll)
     ════════════════════════════════════════════════ */
  const NuvStars = {
    init() {
      const containers = document.querySelectorAll('.nuvora-stars-animate');
      if (!containers.length) return;

      const observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.animate(entry.target);
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0, rootMargin: '0px 0px -60px 0px' }
      );

      containers.forEach(c => observer.observe(c));
    },

    animate(container) {
      const speed = parseInt(container.dataset.speed, 10) || 150;
      container.querySelectorAll('.nuvora-star-anim').forEach((star, i) => {
        setTimeout(() => star.classList.add('is-visible'), i * speed);
      });
    }
  };

  /* ════════════════════════════════════════════════
     REVIEWS CAROUSEL — scroll horizontal nativo no mobile, mesma técnica
     do carrossel de vídeos UGC (scroll-snap + setas que fazem scrollBy).
     A grelha volta a grid no desktop via CSS; este JS não atrapalha lá
     porque as setas/pontos ficam escondidos acima de 750px.
     ════════════════════════════════════════════════ */
  const NuvReviewsCarousel = {
    init() {
      const track = document.getElementById('nuvora-reviews-track');
      if (!track) return;
      this.track = track;
      this.cards = Array.prototype.slice.call(track.querySelectorAll('.nuvora-review-card'));
      if (this.cards.length < 2) return;

      this.dots = Array.prototype.slice.call(document.querySelectorAll('[data-review-dot]'));
      const prevBtn = document.getElementById('nuvora-reviews-prev');
      const nextBtn = document.getElementById('nuvora-reviews-next');

      const step = () => this.cards[0].getBoundingClientRect().width + 14;

      const updateNav = () => {
        const max = track.scrollWidth - track.clientWidth - 2;
        if (prevBtn) prevBtn.disabled = track.scrollLeft <= 2;
        if (nextBtn) nextBtn.disabled = track.scrollLeft >= max;

        if (this.dots.length) {
          const current = Math.round(track.scrollLeft / step());
          const dotIndex = current % this.dots.length;
          this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === dotIndex));
        }
      };

      if (prevBtn) prevBtn.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
      if (nextBtn) nextBtn.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));

      this.dots.forEach(dot => {
        dot.addEventListener('click', () => {
          const index = parseInt(dot.dataset.reviewDot, 10);
          track.scrollTo({ left: index * step(), behavior: 'smooth' });
        });
      });

      track.addEventListener('scroll', updateNav, { passive: true });
      window.addEventListener('resize', updateNav);
      updateNav();
    }
  };

  /* ════════════════════════════════════════════════
     REVIEW PHOTO LIGHTBOX — clicar numa foto de review
     abre-a em ecrã inteiro
     ════════════════════════════════════════════════ */
  const NuvReviewLightbox = {
    init() {
      this.lightbox = document.getElementById('nuvora-review-lightbox');
      if (!this.lightbox) return;
      this.img = document.getElementById('nuvora-review-lightbox-img');
      const closeBtn = document.getElementById('nuvora-review-lightbox-close');

      document.querySelectorAll('.nuvora-review-card__image-btn').forEach(btn => {
        btn.addEventListener('click', () => this.open(btn.dataset.reviewImageFull, btn.dataset.reviewImageAlt));
      });

      closeBtn.addEventListener('click', () => this.close());
      this.lightbox.addEventListener('click', e => {
        if (e.target === this.lightbox) this.close();
      });
      document.addEventListener('keydown', e => {
        if (e.key === 'Escape') this.close();
      });
    },

    open(src, alt) {
      this.img.src = src;
      this.img.alt = alt || '';
      this.lightbox.classList.add('is-open');
      this.lightbox.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    },

    close() {
      this.lightbox.classList.remove('is-open');
      this.lightbox.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      this.img.src = '';
    }
  };

  /* ════════════════════════════════════════════════
     INIT on DOMContentLoaded
     ════════════════════════════════════════════════ */
  function init() {
    NuvoraGallery.init();
    NuvoraVariants.init();
    NuvStickyBar.init();
    NuvCart.init();
    NuvReveal.init();
    NuvCounter.init();
    NuvStars.init();
    NuvReviewsCarousel.init();
    NuvReviewLightbox.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();


/* ═══════════════════════════════════════════════════════════════════
   PDP — OIL KIT (Build your scent kit) — movido do carrinho para a PDP.
   Delegação de eventos em `document` porque o botão "Choose your scents"
   troca conteúdo do painel dinamicamente.
   ═══════════════════════════════════════════════════════════════════ */
if (!window.__nuvoraOilKitInit) {
  window.__nuvoraOilKitInit = true;

  var NUVORA_OILKIT_TIER_PRICE = { 2: 12, 4: 21, 6: 29 };
  var NUVORA_OILKIT_UNIT_PRICE = 11;

  function nuvoraOilkitWidgetOf(el) {
    return el.closest('.nuvora-oilkit');
  }

  function nuvoraOilkitSelected(widget) {
    return Array.prototype.filter.call(
      widget.querySelectorAll('.nuvora-oilkit__thumb'),
      function (t) { return t.dataset.sel === 'true'; }
    );
  }

  function nuvoraOilkitCurrentTarnPrice() {
    var data = window.nuvoraProductData;
    if (!data || !data.variants) return null;
    var variantId = document.getElementById('nuvora-variant-id')
      ? Number(document.getElementById('nuvora-variant-id').value)
      : data.currentVariantId;
    var variant = data.variants.filter(function (v) { return v.id === variantId; })[0];
    return variant ? variant.price / 100 : null;
  }

  /* Total com o kit de óleos incluído — reage logo ao primeiro óleo
     escolhido (não espera a faixa ficar completa), e some assim que a
     selecção volta a zero. O preço mostrado é sempre o da FAIXA
     escolhida (2/4/6), já que os óleos não têm preço unitário próprio —
     escolher 1 de 2 já mostra o total de "2 for £12", por exemplo. */
  function nuvoraOilkitUpdatePriceAddon(hasAnySelected, oilPrice) {
    var addon = document.getElementById('nuvora-price-addon');
    if (!addon) return;
    var tarnPrice = nuvoraOilkitCurrentTarnPrice();
    if (!hasAnySelected || tarnPrice === null) {
      addon.hidden = true;
      addon.textContent = '';
      return;
    }
    var total = tarnPrice + oilPrice;
    addon.textContent = 'Total with scent kit: £' + total.toFixed(2).replace('.00', '');
    addon.hidden = false;
  }

  function nuvoraOilkitUpdate(widget) {
    var tier = Number(widget.dataset.tier);
    Array.prototype.forEach.call(widget.querySelectorAll('.nuvora-oilkit__tier'), function (b) {
      b.dataset.active = String(Number(b.dataset.n) === tier);
    });

    var selected = nuvoraOilkitSelected(widget);
    var ready = selected.length === tier;
    var price = NUVORA_OILKIT_TIER_PRICE[tier];
    var full = tier * NUVORA_OILKIT_UNIT_PRICE;
    var savings = full - price;

    var perBottle = widget.querySelector('[data-role="perbottle"]');
    if (perBottle) {
      perBottle.textContent = 'Save £' + savings;
    }

    // Já não adiciona ao carrinho sozinho — só marca a selecção como
    // pronta. Os óleos seguem junto com o Tarn quando o utilizador
    // carregar no botão principal "Get Your Tarn".
    var cta = widget.querySelector('[data-role="cta"]');
    if (cta) {
      cta.textContent = ready
        ? '✓ ' + tier + ' scents selected'
        : 'Choose ' + (tier - selected.length) + ' more (' + selected.length + '/' + tier + ')';
      cta.classList.toggle('is-ready', ready);
    }

    var savingsEl = widget.querySelector('[data-role="savings"]');
    if (savingsEl) savingsEl.textContent = ready ? 'You save £' + savings + ' on this kit' : '';

    nuvoraOilkitUpdatePriceAddon(selected.length > 0, price);
  }

  /* Lê a selecção actual do kit de óleos (se houver e estiver completa) —
     usado pelo envio do formulário principal para juntar os óleos ao
     Tarn no mesmo pedido. */
  function nuvoraOilkitReadyItems() {
    var widget = document.querySelector('.nuvora-oilkit');
    if (!widget) return [];
    var tier = Number(widget.dataset.tier);
    var selected = nuvoraOilkitSelected(widget);
    if (selected.length !== tier) return [];
    return selected.map(function (t) {
      return { id: Number(t.dataset.variantId), quantity: 1 };
    });
  }
  // Exposto em window — chamado pelo NuvCart (módulo separado, mais
  // acima no ficheiro) ao submeter o formulário principal do Tarn.
  window.nuvoraOilkitReadyItems = nuvoraOilkitReadyItems;

  function nuvoraOilkitExpand(toggleBtn) {
    var widget = nuvoraOilkitWidgetOf(toggleBtn);
    var panel = widget.querySelector('[data-role="panel"]');
    panel.hidden = false;
    toggleBtn.hidden = true;
  }

  function nuvoraOilkitSelectTier(tierBtn) {
    var widget = nuvoraOilkitWidgetOf(tierBtn);
    var n = Number(tierBtn.dataset.n);
    widget.dataset.tier = n;
    var selected = nuvoraOilkitSelected(widget);
    if (selected.length > n) {
      selected.slice(n).forEach(function (t) { t.dataset.sel = 'false'; });
    }
    nuvoraOilkitUpdate(widget);
  }

  function nuvoraOilkitToggleThumb(thumb) {
    var widget = nuvoraOilkitWidgetOf(thumb);
    var tier = Number(widget.dataset.tier);
    var selected = nuvoraOilkitSelected(widget);
    if (thumb.dataset.sel === 'true') {
      thumb.dataset.sel = 'false';
    } else if (selected.length < tier) {
      thumb.dataset.sel = 'true';
    }
    nuvoraOilkitUpdate(widget);
  }

  function nuvoraOilkitLightboxOf(el) {
    var wrap = el.closest('.nuvora-oilkit-wrap');
    return wrap ? wrap.querySelector('.nuvora-oilkit-lightbox') : null;
  }

  function nuvoraOilkitSyncLightboxCta(lightbox, thumb) {
    var cta = lightbox.querySelector('[data-role="lightbox-cta"]');
    var selected = thumb.dataset.sel === 'true';
    cta.dataset.selected = String(selected);
    cta.textContent = selected ? 'Remove from kit' : 'Add to kit';
  }

  function nuvoraOilkitOpenLightbox(zoomBtn) {
    var thumb = zoomBtn.closest('.nuvora-oilkit__thumb');
    var lightbox = nuvoraOilkitLightboxOf(zoomBtn);
    if (!thumb || !lightbox) return;
    var img = thumb.querySelector('img');
    var name = thumb.querySelector('span:last-child').textContent;
    lightbox.dataset.variantId = thumb.dataset.variantId;
    lightbox.querySelector('.nuvora-oilkit-lightbox__img').src = img.src;
    lightbox.querySelector('.nuvora-oilkit-lightbox__img').alt = name;
    lightbox.querySelector('.nuvora-oilkit-lightbox__name').textContent = name;
    nuvoraOilkitSyncLightboxCta(lightbox, thumb);
    lightbox.hidden = false;
  }

  function nuvoraOilkitCloseLightbox(el) {
    var lightbox = el.closest('.nuvora-oilkit-lightbox');
    if (lightbox) lightbox.hidden = true;
  }

  function nuvoraOilkitToggleFromLightbox(lbCta) {
    var lightbox = lbCta.closest('.nuvora-oilkit-lightbox');
    var wrap = lightbox.closest('.nuvora-oilkit-wrap');
    var widget = wrap.querySelector('.nuvora-oilkit');
    var variantId = lightbox.dataset.variantId;
    var thumb = widget.querySelector('.nuvora-oilkit__thumb[data-variant-id="' + variantId + '"]');
    if (!thumb) return;
    var tier = Number(widget.dataset.tier);
    var selected = nuvoraOilkitSelected(widget);
    if (thumb.dataset.sel === 'true') {
      thumb.dataset.sel = 'false';
    } else if (selected.length < tier) {
      thumb.dataset.sel = 'true';
    }
    nuvoraOilkitUpdate(widget);
    nuvoraOilkitSyncLightboxCta(lightbox, thumb);
  }

  // Entrada do cartão do kit de óleos — só anima quando o cartão entra
  // mesmo na vista (scroll), em vez de disparar ao carregar a página.
  // Fica visível por defeito no CSS; só aqui é que se decide
  // esconder-e-revelar.
  function nuvoraInitOilkitReveal() {
    var card = document.querySelector('.nuvora-oilkit');
    if (!card || card.dataset.nuvoraRevealInit) return;
    card.dataset.nuvoraRevealInit = 'true';

    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    card.classList.add('nuvora-oilkit--pending');
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        card.classList.remove('nuvora-oilkit--pending');
        card.classList.add('nuvora-oilkit--reveal');
        observer.disconnect();
      });
    }, { threshold: 0.3 });
    observer.observe(card);
  }

  nuvoraInitOilkitReveal();

  document.addEventListener('click', function (e) {
    if (!e.target) return;

    var zoomBtn = e.target.closest('.nuvora-oilkit__zoom');
    if (zoomBtn) { nuvoraOilkitOpenLightbox(zoomBtn); return; }

    var closeBtn = e.target.closest('.nuvora-oilkit-lightbox__close');
    if (closeBtn) { nuvoraOilkitCloseLightbox(closeBtn); return; }

    var backdrop = e.target.closest('.nuvora-oilkit-lightbox__backdrop');
    if (backdrop) { nuvoraOilkitCloseLightbox(backdrop); return; }

    var lbCta = e.target.closest('.nuvora-oilkit-lightbox [data-role="lightbox-cta"]');
    if (lbCta) { nuvoraOilkitToggleFromLightbox(lbCta); return; }

    var toggleBtn = e.target.closest('.nuvora-oilkit__toggle');
    if (toggleBtn) { nuvoraOilkitExpand(toggleBtn); return; }

    var tierBtn = e.target.closest('.nuvora-oilkit__tier');
    if (tierBtn) { nuvoraOilkitSelectTier(tierBtn); return; }

    var thumb = e.target.closest('.nuvora-oilkit__thumb');
    if (thumb) { nuvoraOilkitToggleThumb(thumb); return; }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var open = document.querySelector('.nuvora-oilkit-lightbox:not([hidden])');
    if (open) open.hidden = true;
  });
}
